import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Breadcrumbs, Button, Chip, Link, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { OpportunityService } from '../../services/opportunity.service';
import {
  Opportunity, OpportunityStage, OPPORTUNITY_STAGES,
  OPPORTUNITY_STAGE_LABELS, isWonStage,
} from '../../interfaces/crm/crm.interface';
import OpportunityForm from './OpportunityForm';
import ActivityTimeline from './ActivityTimeline';
import LogActivityDialog from './LogActivityDialog';
import Field from './Field';
import { useToast } from '../../context/ToastContext';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { breadcrumbsSx } from '../modules/ModuleBreadcrumbs';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { CRM_ATTACHMENT_KINDS } from '../../services/attachment.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

const OpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.CRM_MANAGE);

  const [opp, setOpp] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [activityReload, setActivityReload] = useState(0);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [changingStage, setChangingStage] = useState(false);

  // Lost-reason prompt state.
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await OpportunityService.getById(id);
      setOpp(res);
    } catch (e) {
      toast.showApiError(e, 'Failed to load opportunity');
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  const applyStage = async (stage: OpportunityStage, reason?: string) => {
    if (!opp) return;
    setChangingStage(true);
    try {
      const updated = await OpportunityService.changeStage(opp.id, { stage, lostReason: reason });
      setOpp(updated);
      toast.showSuccess('Stage updated');
    } catch (e) {
      toast.showApiError(e, 'Failed to update stage');
    } finally {
      setChangingStage(false);
    }
  };

  const handleStageSelect = (stage: OpportunityStage) => {
    if (stage === 'LOST') {
      setLostReason('');
      setLostOpen(true);
      return;
    }
    void applyStage(stage);
  };

  const handleConfirmLost = async () => {
    setLostOpen(false);
    await applyStage('LOST', lostReason.trim() || undefined);
  };

  const handleCreateQuotation = async () => {
    if (!opp) return;
    setCreatingQuote(true);
    try {
      const quotation = await OpportunityService.createQuotation(opp.id);
      toast.showSuccess('Quotation created');
      // Hand off to the EXISTING quotation editor.
      navigate(`/sales/quotations/${quotation.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create quotation');
    } finally {
      setCreatingQuote(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!opp) return <Box sx={{ p: 3 }}><Typography>Opportunity not found.</Typography></Box>;

  const hasQuotation = Boolean(opp.quotationId);

  return (
    <Box sx={{ p: 3 }} data-help-page="crm.opportunityDetail.page">
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={breadcrumbsSx}>
        <Link component={RouterLink} to="/modules" underline="hover" color="inherit">All Modules</Link>
        <Link component={RouterLink} to="/modules/crm" underline="hover" color="inherit">CRM</Link>
        <Link component={RouterLink} to="/crm/pipeline" underline="hover" color="inherit">Pipeline</Link>
        <Typography color="text.primary" fontSize="inherit">{opp.title}</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{opp.title}</Typography>
          <Chip label={OPPORTUNITY_STAGE_LABELS[opp.stage]} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canManage && <Button data-help="crm.opportunity.edit" variant="outlined" onClick={() => setEditOpen(true)}>Edit</Button>}
          {hasQuotation ? (
            <Button data-help="crm.opportunityDetail.viewQuotation" variant="contained" onClick={() => navigate(`/sales/quotations/${opp.quotationId}`)}>
              View Quotation
            </Button>
          ) : (
            <Button
              data-help="crm.opportunity.createQuotation"
              variant="contained"
              disabled={!canManage || !isWonStage(opp.stage) || creatingQuote}
              onClick={handleCreateQuotation}
            >
              {creatingQuote ? <CircularProgress size={20} /> : 'Create Quotation'}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Details</Typography>
            <Field label="Customer" value={opp.customerName} />
            <Field label="Estimated Amount" value={opp.estimatedAmount != null ? formatMoney(opp.estimatedAmount, 'BDT') : undefined} />
            <Field label="Expected Close Date" value={opp.expectedCloseDate ? formatDate(opp.expectedCloseDate) : undefined} />
            {opp.lostReason && <Field label="Lost Reason" value={opp.lostReason} />}
            <Field label="Notes" value={opp.notes} />

            <Box sx={{ mt: 2 }}>
              <TextField
                data-help="crm.opportunity.stage"
                select
                fullWidth
                label="Stage"
                value={opp.stage}
                onChange={e => handleStageSelect(e.target.value as OpportunityStage)}
                disabled={changingStage || !canManage}
              >
                {OPPORTUNITY_STAGES.map(s => (
                  <MenuItem key={s} value={s}>{OPPORTUNITY_STAGE_LABELS[s]}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                data-help="crm.opportunity.markWon"
                size="small"
                variant="outlined"
                color="success"
                disabled={!canManage || changingStage || opp.stage === 'WON'}
                onClick={() => applyStage('WON')}
              >
                Mark Won
              </Button>
              <Button
                data-help="crm.opportunity.markLost"
                size="small"
                variant="outlined"
                color="error"
                disabled={!canManage || changingStage || opp.stage === 'LOST'}
                onClick={() => handleStageSelect('LOST')}
              >
                Mark Lost
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Activity</Typography>
              {canManage && <Button data-help="crm.opportunity.logActivity" size="small" variant="outlined" onClick={() => setLogOpen(true)}>Log Activity</Button>}
            </Box>
            <Divider sx={{ mb: 1 }} />
            <ActivityTimeline ownerType="OPPORTUNITY" ownerId={opp.id} reloadKey={activityReload} />
          </Paper>
        </Grid>
      </Grid>

      <OpportunityForm
        open={editOpen}
        opportunity={opp}
        onClose={() => setEditOpen(false)}
        onSaved={updated => setOpp(updated)}
      />

      <LogActivityDialog
        open={logOpen}
        ownerType="OPPORTUNITY"
        ownerId={opp.id}
        onClose={() => setLogOpen(false)}
        onLogged={() => setActivityReload(k => k + 1)}
      />

      <Dialog open={lostOpen} onClose={() => setLostOpen(false)} fullWidth maxWidth="xs" data-help-page="crm.opportunityDetail.lostDialog">
        <DialogTitle>Mark as Lost</DialogTitle>
        <DialogContent data-help-section="crm.opportunityDetail.lostDialog.section.reason">
          <TextField
            data-help="crm.opportunityDetail.lostDialog.reason"
            autoFocus
            fullWidth
            label="Lost Reason (optional)"
            value={lostReason}
            onChange={e => setLostReason(e.target.value)}
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="crm.opportunityDetail.lostDialog.cancel" onClick={() => setLostOpen(false)}>Cancel</Button>
          <Button data-help="crm.opportunityDetail.lostDialog.confirm" variant="contained" color="error" onClick={handleConfirmLost}>Mark Lost</Button>
        </DialogActions>
      </Dialog>
      <Box data-help-section="sales.opportunity.attachments">
        <AttachmentsPanel entityType="OPPORTUNITY" entityId={opp.id} canManage={canManage} kinds={CRM_ATTACHMENT_KINDS} />
      </Box>
    </Box>
  );
};

export default OpportunityDetail;
